// components/LeadForm.tsx
import React, { useState } from 'react';
import { User, Mail, Phone, Building, MessageSquare, Sparkles } from 'lucide-react';
import type { LeadFormData } from '../types/chat.types';

interface LeadFormProps {
  onSubmit: (data: LeadFormData) => void;
}

export const LeadForm: React.FC<LeadFormProps> = ({ onSubmit }) => {
  const [formData, setFormData] = useState<LeadFormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  
  const [errors, setErrors] = useState<Partial<LeadFormData>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<LeadFormData> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Imię jest wymagane';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email jest wymagany';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Nieprawidłowy format email';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Wyczyść błąd dla tego pola
    if (errors[name as keyof LeadFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-6 bg-zinc-800 rounded-xl border border-zinc-700">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-8 h-8 text-orange-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-200 mb-2">
          Rozpocznij rozmowę z AI
        </h3>
        <p className="text-sm text-gray-400">
          Zostaw swoje dane, aby otrzymać spersonalizowaną pomoc
        </p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          <User className="w-4 h-4 inline mr-1 text-orange-500" />
          Imię i nazwisko *
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`w-full px-3 py-2 bg-zinc-900 border rounded-lg focus:outline-none focus:ring-2 text-gray-200 placeholder-gray-500 ${
            errors.name ? 'border-red-500 focus:ring-red-500' : 'border-zinc-700 focus:ring-orange-500'
          }`}
          placeholder="Jan Kowalski"
        />
        {errors.name && (
          <p className="text-red-500 text-xs mt-1">{errors.name}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          <Mail className="w-4 h-4 inline mr-1 text-orange-500" />
          Email *
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full px-3 py-2 bg-zinc-900 border rounded-lg focus:outline-none focus:ring-2 text-gray-200 placeholder-gray-500 ${
            errors.email ? 'border-red-500 focus:ring-red-500' : 'border-zinc-700 focus:ring-orange-500'
          }`}
          placeholder="jan@firma.pl"
        />
        {errors.email && (
          <p className="text-red-500 text-xs mt-1">{errors.email}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          <Phone className="w-4 h-4 inline mr-1 text-orange-500" />
          Telefon
        </label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-200 placeholder-gray-500"
          placeholder="123 456 789"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          <Building className="w-4 h-4 inline mr-1 text-orange-500" />
          Firma
        </label>
        <input
          type="text"
          name="company"
          value={formData.company}
          onChange={handleChange}
          className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-200 placeholder-gray-500"
          placeholder="Nazwa firmy"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          <MessageSquare className="w-4 h-4 inline mr-1 text-orange-500" />
          W czym możemy pomóc?
        </label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={3}
          className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none text-gray-200 placeholder-gray-500"
          placeholder="Opisz krótko czego potrzebujesz..."
        />
      </div>

      <button
        type="submit"
        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 px-4 rounded-lg hover:shadow-lg hover:shadow-orange-500/25 transition-all font-medium"
      >
        Rozpocznij rozmowę z AI
      </button>
      
      <p className="text-xs text-gray-500 text-center">
        Wysyłając formularz zgadzasz się na przetwarzanie danych osobowych zgodnie z RODO
      </p>
    </form>
  );
};